import z from "zod";
import { AvatarSchema } from "rdx/modules/users/schemas/avatar";
import { PaymentStatuses } from "../types";

export enum CompServiceSlugs {
  ENTERPRISE_CLOSER_COMPED = "enterprise_closer_comped",
  CLOSER_COMPED = "powur_closer_comped",
  POWUR_ENTERPRISE_PRO_COMPED = "powur_enterprise_pro_comped",
}

export enum ServiceSlugs {
  POWUR_CLOSER = "powur_closer",
  POWUR_ENTERPRISE = "powur_enterprise",
  POWUR_ENERGY = "powur_energy",
  POWUR_ENTERPRISE_PRO = "powur_enterprise_pro",
}

export enum EntServiceSlugs {
  ENTERPRISE_CLOSER = "enterprise_closer",
  ENTERPRISE_CONSULTANT = "enterprise_consultant",
  POWUR_ENTERPRISE_CO_OWNER = "powur_enterprise_pro_co_owner",
}

export const EntServiceSlugsSchema = z.nativeEnum(EntServiceSlugs);

export enum EntStaffSlugs {
  ENTERPRISE_SUPPORT = "enterprise_support",
  ENTERPRISE_MANAGER = "enterprise_manager",
}

export const EntStaffSlugsSchema = z.nativeEnum(EntStaffSlugs);

export enum PowurLineEntSlugs {
  POWURLINE_ENTERPRISE_MANAGER = "powurline_enterprise_manager",
  POWURLINE_ENTERPRISE_SUPPORT = "powurline_enterprise_support",
  POWURLINE_ENTERPRISE_CLOSER = "powurline_enterprise_closer",
  POWURLINE_ENTERPRISE_CONSULTANT = "powurline_enterprise_consultant",
}

export const PowurLineEntSlugsSchema = z.nativeEnum(PowurLineEntSlugs);

export const StaffSlugsSchema = z.union([
  EntStaffSlugsSchema,
  z.literal(PowurLineEntSlugs.POWURLINE_ENTERPRISE_MANAGER),
  z.literal(PowurLineEntSlugs.POWURLINE_ENTERPRISE_SUPPORT),
]);

export const AllServiceSlugsSchema = z.union([
  z.nativeEnum(ServiceSlugs),
  z.nativeEnum(EntServiceSlugs),
  z.nativeEnum(EntStaffSlugs),
  z.nativeEnum(CompServiceSlugs),
  z.nativeEnum(PowurLineEntSlugs),
]);

export const UpgradeEligibilitySchema = z.object({
  met: z.boolean(),
  current: z.number(),
  required: z.number(),
});

export const ServiceSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: AllServiceSlugsSchema,
  fee: z.number(),
  monthly: z.number(),
  groupData: z.object({ enterpriseCategory: z.string() }).or(z.string()),
  displayData: z.object({ enterpriseDisplayName: z.string().nullish() }).or(z.string().nullish()),
  feeRequired: z.boolean(),
  upgradeEligibility: UpgradeEligibilitySchema.optional(),
});

export const EnrollmentServiceSchema = ServiceSchema.omit({ displayData: true, feeRequired: true }).merge(
  z.object({
    description: z.string().nullable(),
    currency: z.string(),
    enabled: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    startAt: z.string().nullable(),
    endAt: z.string().nullable(),
    requiresEducation: z.boolean().nullish(),
    parentId: z.number().nullable(),
    unique: z.boolean(),
    tags: z.array(z.string()),
    replaceServiceId: z.number().nullable(),
    assignableServiceIds: z.array(z.number()),
    slug: AllServiceSlugsSchema,
  }),
);

export const EnterpriseMemberSchema = z.lazy(() =>
  z.object({
    id: z.number(),
    fullName: z.string(),
    service: EnrollmentServiceSchema,
    endDay: z.string().nullish(),
    avatar: AvatarSchema.nullish(),
    sellerAvatar: AvatarSchema.nullish(),
    seatId: z.number().nullish(),
    enterpriseId: z.number().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    vanityName: z.string().nullish(),
    locale: z.string().nullish(),
    spokenLanguages: z.array(z.string().nullable()).nullish(),
    paymentsPausedAt: z.string().nullish(),
    terminatedAt: z.string().nullish(),
    roles: z.array(z.string()).nullish(),
  }),
);

export const UncompServiceSchema = z.object({
  slug: AllServiceSlugsSchema,
  name: z.string(),
  monthly: z.number(),
});

export const EnrollmentSchema = z.object({
  id: z.number(),
  startDay: z.string(),
  endDay: z.string().nullable(),
  startedAt: z.string(),
  paymentStatus: z.nativeEnum(PaymentStatuses),
  service: EnrollmentServiceSchema,
  downgradeDay: z.string().nullable(),
  inactive: z.boolean().nullable(),
  activeUntil: z.string().nullable(),
  currentPeriodStart: z.string().nullish(),
  enterpriseMembers: z
    .array(
      z.object({
        user: EnterpriseMemberSchema,
      }),
    )
    .nullish(),
  enterpriseLiveMembers: z.array(z.lazy(() => EnterpriseMemberSchema)).nullish(),
  comped: z.boolean(),
  paymentRequired: z.boolean().optional(),
  uncompService: UncompServiceSchema.nullish(),
});

export const FlatEnrollmentSchema = EnrollmentSchema.omit({ service: true })
  .merge(EnrollmentServiceSchema)
  .merge(
    z.object({
      role: z.string().nullish(),
      slug: AllServiceSlugsSchema,
    }),
  );

export const CardInfoSchema = z.object({
  last4: z.string(),
  expYear: z.number(),
  expMonth: z.number(),
});

export const SeatServiceSchema = EnrollmentServiceSchema.merge(z.object({ slug: AllServiceSlugsSchema }));

export const GetEnrollmentResponseBodySchema = z.object({
  monthlyTotal: z.number(),
  enrollment: EnrollmentSchema.nullish(),
  availableServices: z.array(ServiceSchema.merge(z.object({ slug: AllServiceSlugsSchema }))),
  balance: z.number(),
  cardInfo: CardInfoSchema.partial().nullable(),
  seatService: SeatServiceSchema.nullable(),
});

export const GetEnrollmentResponseSchema = z.object({
  status: z.number(),
  body: GetEnrollmentResponseBodySchema,
});

export const BannerSchema = z.object({
  label: z.string(),
  props: z.record(z.unknown()).optional(),
  show: z.boolean(),
});

export const UpdateEnrollmentResponseBodySchema = GetEnrollmentResponseBodySchema.pick({
  enrollment: true,
  cardInfo: true,
}).merge(z.object({ banners: z.array(BannerSchema).optional() }));

export const UpdateEnrollmentResponseSchema = z.object({
  status: z.number(),
  body: UpdateEnrollmentResponseBodySchema,
});

export const UncompEnrollmentResponseBodySchema = z.object({
  enrollment: EnrollmentSchema,
  cardInfo: CardInfoSchema.partial().nullable(),
});

export const UncompEnrollmentResponseSchema = z.object({
  status: z.number(),
  body: UncompEnrollmentResponseBodySchema,
});

export const UpgradePreviewResponseSchema = z.object({
  accumulatedChanges: z.number(),
  nextPeriodTotal: z.number(),
  theseChanges: z.number(),
  total: z.number(),
  data: z.object({
    value: z.number(),
    expires: z.number(),
    prorationKey: z.string(),
  }),
});
